/**
 * 軽タスクリストの「テスト可否」列 — フロント/バック共有の単一 SoT。
 *
 * 軽タスクリストページ（`/projects/light-tasks`）の「アサイン済み軽タスク」テーブルに出す
 * 「テスト可否」列の選択肢と編集権限述語を集約する。
 * - API の OpenAPI schema (`apps/api/product/src/features/project/api/schemas.ts`)
 * - API の Firestore 読み書き (`infrastructure/firestore-light-task-testability-repository.ts`)
 * - Web の表示/編集 (`components/features/project/LightTaskListTab.tsx`)
 * がすべてこの 1 ファイルから import し、選択肢と権限ルールが二重定義されないようにする。
 *
 * この列は当面 Backlog カスタムフィールドや他 DB と接続せず、cortex 側の Firestore にだけ
 * 保存する画面固有の値（他システムへの書き戻し・参照は行わない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 軽タスク「テスト可否」列の選択肢定義と編集権限述語の単一 SoT
 * @graph-connects none
 */
import { ENGINEERING_DIVISION } from '@cortex/core-types';
import { PMO_TEAM_NAME } from '@cortex/domain-product-innovation-lab-combat-power-scope';
/**
 * 「テスト可否」の選択肢（プルダウンの表示順）。
 * - `OK`     : テスト実施可能（緑）
 * - `準備中` : テスト準備中（青）
 * - `不可`   : テスト実施不可（グレー）
 *
 * 未設定は `null` で表し、この配列には含めない（プルダウン側で「未設定」を別途出す）。
 * @graph-connects none
 */
export const LIGHT_TASK_TESTABILITY_VALUES = ['OK', '準備中', '不可'];
/** @graph-connects none */
const LIGHT_TASK_TESTABILITY_SET = new Set(LIGHT_TASK_TESTABILITY_VALUES);
/**
 * 任意の値が `LightTaskTestability` かを判定する型ガード。Firestore に想定外の文字列が
 * 残っていた場合に「未設定」へ倒すために使う。
 * @graph-connects none
 */
export function isLightTaskTestability(value) {
    return typeof value === 'string' && LIGHT_TASK_TESTABILITY_SET.has(value);
}
/**
 * 「テスト可否」を編集できるかの判定。**閲覧は全メンバー可**で、書き込みだけをここで絞る。
 * 許可条件は次のいずれか:
 *
 * 1. cortex 管理者（`admin` ロールを保持）— Div を問わず常に許可
 * 2. PMOチーム所属（`teamName === 'pi_pmot'`）— ロールを問わない（PMOT メンバー全員）
 * 3. `division === 'pi_div'`（PDG / ACE / PMO / IDT が属するエンジニア組織）かつ `team_leader`
 *
 * したがって次はいずれも **閲覧のみ**:
 * - PI Div の一般メンバー（`member` / `architect` / `dle` / `tech_lead` 単独保持）
 * - 横断 Div のチームリーダー（`ac_div` / `mall_div` / `log_div` × `team_leader`）
 * - 横断 PMO（PMOチーム所属ではない `pmo` ロール保持者）
 * - `external`（portalRole 未設定の全社員フォールバック）
 *
 * `team_leader` は Div を問わず存在するため、PI Div 所属であることを `division` 側で必ず
 * 併せて確認する。複数ロール保持時は「許可ロールを 1 つでも持つか」の existential チェックで、
 * 優先順位の概念はない。frontend（プルダウンの出し分け）と backend（PUT の 403）が同一述語を
 * 参照するための SSOT。重複定義は禁止。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 軽タスク「テスト可否」の編集権限ルール (admin / pi_pmot 所属 / pi_div + team_leader)
 * @graph-connects none
 */
export function canEditLightTaskTestability(identity) {
    const roles = identity.portalRoles ?? [];
    if (roles.includes('admin'))
        return true;
    if (identity.teamName === PMO_TEAM_NAME)
        return true;
    return identity.division === ENGINEERING_DIVISION && roles.includes('team_leader');
}
//# sourceMappingURL=light-task.js.map